import { TrashIcon, XMarkIcon } from '@heroicons/react/24/outline'
import FileInputUploadField from 'components/styledFormElements/FileInputUploadField'
import InputField from 'components/styledFormElements/InputField'
import { FileSizeLimit } from 'constants/file-validation'
import React, { useCallback, useEffect, useMemo } from 'react'
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { createQuestionFormSchema } from '../validation'
import { ImagePath } from 'constants/other'
import { useUploadFiles } from 'hooks/upload'
import { getImageSrc } from 'services/util'

export type QuestionOptionRow = {
    label: string
    variable_name: string
    value: string
}

export type CreateQuestionSavePayload = {
    question: string
    description: string
    chipImage: string
    isMultiSelect: 0 | 1
    options: QuestionOptionRow[]
}

type CreateQuestionFormValues = Omit<CreateQuestionSavePayload, 'chipImage'> & {
    chipImage: string | File
}

export interface CreateQuestionsModalProps {
    open: boolean
    onClose: () => void
    initialData?: CreateQuestionSavePayload | null
    onSave?: (data: CreateQuestionSavePayload) => void
}

const EMPTY_OPTION: QuestionOptionRow = {
    label: '',
    variable_name: '',
    value: '',
}

const EMPTY_FORM_VALUES: CreateQuestionFormValues = {
    question: '',
    description: '',
    chipImage: '',
    isMultiSelect: 0,
    options: [{ ...EMPTY_OPTION }],
}

/** Stable key so opening the modal does not reset on every parent re-render with a new object reference. */
function initialDataSyncKey(data: CreateQuestionSavePayload | null | undefined): string {
    if (!data) return 'create'
    return JSON.stringify({
        question: data.question,
        description: data.description,
        chipImage: data.chipImage ?? '',
        isMultiSelect: data.isMultiSelect,
        options: data.options,
    })
}

function normalizeInitialValues(
    data: CreateQuestionSavePayload | null | undefined
): CreateQuestionFormValues {
    if (!data) {
        return {
            ...EMPTY_FORM_VALUES,
            options: [{ ...EMPTY_OPTION }],
        }
    }

    const options =
        data.options?.length > 0
            ? data.options.map((o) => ({
                label: o.label ?? '',
                variable_name: o.variable_name ?? '',
                value: o.value ?? '',
            }))
            : [{ ...EMPTY_OPTION }]

    return {
        question: data.question ?? '',
        description: data.description ?? '',
        chipImage: data.chipImage ?? '',
        isMultiSelect: data.isMultiSelect === 1 ? 1 : 0,
        options,
    }
}

const CreateQuestionModal: React.FC<CreateQuestionsModalProps> = ({
    open,
    initialData,
    onClose,
    onSave,
}) => {
    const uploadMutation = useUploadFiles()
    const {
        register,
        control,
        handleSubmit,
        setValue,
        clearErrors,
        reset,
        formState: { errors },
    } = useForm<CreateQuestionFormValues>({
        mode: 'onTouched',
        defaultValues: EMPTY_FORM_VALUES,
        resolver: yupResolver(createQuestionFormSchema),
    })

    const { fields, append, remove } = useFieldArray({
        control,
        name: 'options',
    })

    const watchedChipImage = useWatch({ control, name: 'chipImage' })
    const [chipImagePreview, setChipImagePreview] = React.useState<string | null>(null)
    const [existingChipImage, setExistingChipImage] = React.useState<string | null>(null)

    const syncKey = useMemo(() => initialDataSyncKey(initialData ?? undefined), [initialData])

    useEffect(() => {
        if (!open) return

        const values = normalizeInitialValues(initialData ?? undefined)
        reset(values)

        const chip = values.chipImage
        if (typeof chip === 'string' && chip) {
            setExistingChipImage(null)
            setChipImagePreview(getImageSrc(chip))
        } else if (chip instanceof File) {
            setExistingChipImage(null)
        } else {
            setExistingChipImage(null)
            setChipImagePreview(null)
        }
    }, [open, syncKey, reset])

    useEffect(() => {
        if (!watchedChipImage) {
            setChipImagePreview(null)
            return
        }

        if (watchedChipImage instanceof File) {
            const url = URL.createObjectURL(watchedChipImage)
            setChipImagePreview(url)

            return () => URL.revokeObjectURL(url)
        }

        if (typeof watchedChipImage === 'string') {
            setChipImagePreview(getImageSrc(watchedChipImage))
        }
    }, [watchedChipImage])

    const closeAndReset = useCallback(() => {
        reset(EMPTY_FORM_VALUES)
        setExistingChipImage(null)
        setChipImagePreview(null)
        onClose()
    }, [onClose, reset])

    const onSubmit = useCallback(
        async (data: CreateQuestionFormValues) => {
            let chipImage: string = String(data.chipImage ?? '').trim()
            if (data.chipImage instanceof File) {
                const fd = new FormData()
                fd.append('file', data.chipImage)
                fd.append('customPath', ImagePath.ToolQuestionChip)
                const res = await uploadMutation.mutateAsync({ data: fd })
                chipImage = res?.data?.data?.key || ''
            }

            const payload: CreateQuestionSavePayload = {
                ...data,
                chipImage,
            }

            onSave?.(payload)
            reset(EMPTY_FORM_VALUES)
            setExistingChipImage(null)
            setChipImagePreview(null)
            onClose()
        },
        [onClose, onSave, reset, uploadMutation]
    )

    const handleCancel = useCallback(() => {
        closeAndReset()
    }, [closeAndReset])

    const modalTitle = useMemo(
        () => (initialData ? 'Edit Questions' : 'Create Questions'),
        [initialData]
    )

    if (!open) return null

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-screen items-center justify-center px-4">
                <div
                    className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                    onClick={handleCancel}
                    aria-hidden
                />

                <div className="relative w-full max-w-2xl max-h-[85vh] flex flex-col overflow-auto rounded-lg bg-white dark:bg-slate-800 p-6 shadow-xl transition-all">
                    <div className="mb-6">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                            {modalTitle}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                            Manage tool question details and configurations
                        </p>
                        <button
                            onClick={handleCancel}
                            type="button"
                            className="absolute top-4 right-4 bg-blue-100 dark:bg-slate-700 dark:text-white text-blue-900 hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 rounded-full p-1 inline-flex items-center"
                            aria-label="Close"
                        >
                            <XMarkIcon className="h-4 w-4" />
                        </button>
                    </div>

                    <form
                        onSubmit={handleSubmit(onSubmit)}
                        className="flex flex-col flex-1 min-h-0 space-y-4"
                    >
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <InputField
                                    label="Question"
                                    required
                                    register={register('question')}
                                    error={errors.question?.message}
                                    placeholder="Enter tool question"
                                />
                            </div>
                            <div>
                                <InputField
                                    label="Description"
                                    required
                                    register={register('description')}
                                    error={errors.description?.message}
                                    placeholder="Enter question description"
                                />
                            </div>
                        </div>

                        <div>
                            <FileInputUploadField
                                label="Chip Image"
                                fieldName="chipImage"
                                register={register('chipImage') as any}
                                errors={errors}
                                watchFile={watchedChipImage as any}
                                onFileSelect={(file) => {
                                    if (!file) {
                                        setValue('chipImage', '')
                                        setChipImagePreview(null)
                                        setExistingChipImage(null)
                                        return
                                    }
                                    setValue('chipImage', file)
                                    setExistingChipImage(null)
                                    clearErrors('chipImage')
                                }}
                                preview={chipImagePreview}
                                allowedTypes={FileSizeLimit.thumbnail.allowedTypes}
                                tooltip={FileSizeLimit.thumbnail.tooltipItems as any}
                                fileSizeLimit={FileSizeLimit.thumbnail}
                                required
                            />
                        </div>

                        <div className="flex items-center justify-between py-2">
                            <label className="text-sm font-medium text-gray-700 mb-2 dark:text-gray-300">
                                Allow Multiple Selection
                            </label>
                            <Controller
                                control={control}
                                name="isMultiSelect"
                                render={({ field: { value, onChange } }) => (
                                    <button
                                        type="button"
                                        onClick={() => onChange(value ? 0 : 1)}
                                        className={`relative inline-flex h-5 w-10 items-center rounded-full transition ${value ? 'bg-blue-600' : 'bg-gray-300'
                                            }`}
                                        aria-pressed={value === 1}
                                    >
                                        <span
                                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${value ? 'translate-x-5' : 'translate-x-1'
                                                }`}
                                        />
                                    </button>
                                )}
                            />
                        </div>

                        <div className="mt-4 border border-gray-200 dark:border-slate-700 rounded-lg p-4 bg-gray-50 dark:bg-slate-900/30">
                            <div className="flex items-center justify-between mb-2">
                                <label className="text-sm font-medium text-gray-700 mb-2 dark:text-gray-300">
                                    Options
                                </label>
                                <button
                                    type="button"
                                    onClick={() => append({ ...EMPTY_OPTION })}
                                    className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-700 hover:bg-blue-200"
                                >
                                    + Add Option
                                </button>
                            </div>

                            <div className="grid grid-cols-12 gap-2 text-xs font-medium text-gray-700 dark:text-gray-300 px-2 mb-1">
                                <div className="col-span-4">Label</div>
                                <div className="col-span-3">Variable</div>
                                <div className="col-span-4">Value</div>
                                <div className="col-span-1" />
                            </div>

                            <div className="space-y-2 max-h-[220px] overflow-y-auto pr-1 scrollbar-thin">
                                {fields.map((field, index) => (
                                    <div
                                        key={field.id}
                                        className="grid grid-cols-12 gap-2 items-center"
                                    >
                                        <div className="col-span-4 flex flex-col">
                                            <input
                                                {...register(`options.${index}.label` as const)}
                                                className="w-full text-sm px-2 py-1 border rounded"
                                                placeholder="Label"
                                            />
                                            {errors.options?.[index]?.label?.message && (
                                                <p className="text-xs text-red-500 mt-0.5">
                                                    {errors.options[index]?.label?.message}
                                                </p>
                                            )}
                                        </div>
                                        <div className="col-span-3 flex flex-col">
                                            <input
                                                {...register(
                                                    `options.${index}.variable_name` as const
                                                )}
                                                className="w-full text-sm px-2 py-1 border rounded"
                                                placeholder="variable_name"
                                            />
                                            {errors.options?.[index]?.variable_name?.message && (
                                                <p className="text-xs text-red-500 mt-0.5">
                                                    {errors.options[index]?.variable_name?.message}
                                                </p>
                                            )}
                                        </div>
                                        <div className="col-span-4 flex flex-col">
                                            <input
                                                {...register(`options.${index}.value` as const)}
                                                className="w-full text-sm px-2 py-1 border rounded"
                                                placeholder="Value"
                                            />
                                            {errors.options?.[index]?.value?.message && (
                                                <p className="text-xs text-red-500 mt-0.5">
                                                    {errors.options[index]?.value?.message}
                                                </p>
                                            )}
                                        </div>
                                        <div className="col-span-1 flex justify-center">
                                            <button
                                                type="button"
                                                onClick={() => remove(index)}
                                                disabled={fields.length === 1}
                                                title="Delete option"
                                            >
                                                <TrashIcon className="w-4 h-4 text-red-600" />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>

                        </div>

                        <div className="flex justify-end gap-2 pt-2 border-t border-gray-200 dark:border-slate-700 shrink-0">
                            <button
                                type="button"
                                onClick={handleCancel}
                                className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:border-slate-600 dark:bg-slate-700 dark:text-gray-200 dark:hover:bg-slate-600"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                            >
                                Save
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    )
}

export default CreateQuestionModal